import { Helmet } from "react-helmet-async";
import { DEFAULT_OG_IMAGE, DEFAULT_SITE_NAME, SITE_CANONICAL_ORIGIN } from "../seo/constants";
import {
    DEFAULT_LOCALE,
    Locale,
    PUBLISHED_LOCALES,
    isPublishedLocale,
    localizedPath,
    splitLocale,
} from "../seo/locales";

export type PageSeoProps = {
    title: string;
    description: string;
    /** Current route path, e.g. from useLocation().pathname */
    path: string;
    /** If set, canonical and og:url use this path instead (avoid duplicate /pro vs /valorant-pro-crosshairs). */
    canonicalPath?: string;
    /**
     * Current catalog page. Pages past 1 must canonicalise to themselves — pointing them at
     * page 1 tells the engine the deeper pages are duplicates and drops their crosshairs.
     */
    page?: number;
    keywords?: string;
    ogImage?: string;
    ogType?: "website" | "article";
    noindex?: boolean;
    /** With `noindex`: keep following the page's links, so it still passes them link equity. */
    noindexFollow?: boolean;
    /**
     * Set false on a page that canonicalises to another URL. hreflang belongs on canonical
     * pages only; on the others it advertises translations of a URL that is not indexed.
     */
    hreflang?: boolean;
};

function normalizePath(p: string): string {
    if (!p.startsWith("/")) return `/${p}`;
    return p;
}

const OG_LOCALE: Record<Locale, string> = {
    en: "en_US",
    tr: "tr_TR",
    ru: "ru_RU",
};

export function PageSeo({
    title,
    description,
    path,
    canonicalPath,
    page,
    keywords,
    ogImage = DEFAULT_OG_IMAGE,
    ogType = "website",
    noindex,
    noindexFollow,
    hreflang = true,
}: PageSeoProps) {
    const urlPath = normalizePath(canonicalPath ?? path);
    const { locale, basePath } = splitLocale(urlPath);
    const pageSuffix = page && page > 1 ? `?page=${page}` : "";
    const canonical = `${SITE_CANONICAL_ORIGIN}${urlPath}${pageSuffix}`;

    // A locale that is routable but not yet translated must not be indexed.
    const shouldNoindex = noindex || !isPublishedLocale(locale);

    // Only translated locales are advertised — pointing hreflang at an untranslated
    // page tells the engine a translation exists when it does not.
    const alternates = hreflang
        ? PUBLISHED_LOCALES.map((l) => ({
              hrefLang: l,
              href: `${SITE_CANONICAL_ORIGIN}${localizedPath(basePath, l)}${pageSuffix}`,
          }))
        : [];

    return (
        <Helmet prioritizeSeoTags>
            <html lang={locale} />
            <title>{title}</title>
            <meta name="description" content={description} />
            {keywords ? <meta name="keywords" content={keywords} /> : null}
            <link rel="canonical" href={canonical} />
            {alternates.length > 1
                ? alternates.map((a) => (
                      <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href} />
                  ))
                : null}
            {alternates.length > 1 ? (
                <link
                    rel="alternate"
                    hrefLang="x-default"
                    href={`${SITE_CANONICAL_ORIGIN}${localizedPath(basePath, DEFAULT_LOCALE)}${pageSuffix}`}
                />
            ) : null}
            <meta property="og:type" content={ogType} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:site_name" content={DEFAULT_SITE_NAME} />
            <meta property="og:locale" content={OG_LOCALE[locale]} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />
            {shouldNoindex ? (
                <meta name="robots" content={noindexFollow && noindex ? "noindex, follow" : "noindex, nofollow"} />
            ) : (
                <meta name="robots" content="index, follow" />
            )}
        </Helmet>
    );
}
